import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Download, ShieldCheck, Laptop, Lock, 
  ExternalLink, Sparkles, MessageCircle, HardDrive
} from 'lucide-react';
import { 
  CURRENT_VERSION, 
  DOWNLOAD_FILENAME, 
  DOWNLOAD_SIZE, 
  RELEASE_DATE, 
  RELEASE_CONFIG,
  GOOGLE_DRIVE_DOWNLOAD_URL,
  GOOGLE_DRIVE_VIEW_URL
} from '../config/version';
import contentData from '../data/website_content.json';
import { useRouter } from '../context/RouterContext';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DownloadModal: React.FC<DownloadModalProps> = ({ isOpen, onClose }) => {
  const { navigate } = useRouter();
  const { contact } = contentData;

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 z-[250] flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="download-modal-title"
        >
          {/* Backdrop blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-obsidian/85 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl bg-charcoal border border-rose/25 rounded-3xl shadow-elevated overflow-hidden z-10 my-8"
          >
            {/* Ambient Top Glow (Signature Gradient) */}
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-signature" />
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-40 bg-rose/10 rounded-full blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="relative p-6 sm:p-8 pb-5 border-b border-white/[0.08] flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-action p-[1.5px] shadow-sm shrink-0">
                  <div className="w-full h-full bg-charcoal rounded-[14px] flex items-center justify-center">
                    <picture>
                      <source srcSet="/brand/app_icon_32.webp 1x, /brand/app_icon_64.webp 2x" type="image/webp" />
                      <img 
                        src="/brand/app_icon_32.webp" 
                        alt="Lashary Logo" 
                        width="32"
                        height="32"
                        className="w-8 h-8 object-contain"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = 'none';
                        }}
                      />
                    </picture>
                  </div>
                </div>

                <div>
                  <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-rose/15 border border-rose/30 text-rose text-[11px] font-mono font-bold mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-jade animate-pulse" />
                    COMPILED PRODUCTION RUNTIME v{CURRENT_VERSION}
                  </div>
                  <h2 id="download-modal-title" className="text-xl sm:text-2xl font-black text-ivory tracking-tight">
                    Lashary FB Market Suite
                  </h2>
                  <p className="text-xs text-muted font-sans mt-0.5">
                    Official 64-Bit Desktop Software for Windows 10 / 11
                  </p>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.08] flex items-center justify-center text-muted hover:text-white transition-all cursor-pointer shrink-0"
                aria-label="Close download modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 sm:p-8 space-y-6">
              
              {/* Runtime Technical Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                  <span className="block text-[10px] font-mono text-muted uppercase tracking-wider">Installer Size</span>
                  <span className="text-sm font-bold font-mono text-ivory mt-0.5 block">{DOWNLOAD_SIZE}</span>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                  <span className="block text-[10px] font-mono text-muted uppercase tracking-wider">Architecture</span>
                  <span className="text-sm font-bold font-mono text-apricot mt-0.5 block">Win64 Native</span>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                  <span className="block text-[10px] font-mono text-muted uppercase tracking-wider">Security</span>
                  <span className="text-sm font-bold font-mono text-jade mt-0.5 block">Verified Build</span>
                </div>
                <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                  <span className="block text-[10px] font-mono text-muted uppercase tracking-wider">Privacy</span>
                  <span className="text-sm font-bold font-mono text-ivory mt-0.5 block">100% Local PC</span>
                </div>
              </div>

              {/* Primary 1-Click Native Direct Download Button (Rose → Vermilion) */}
              <div className="space-y-2">
                <a
                  href={GOOGLE_DRIVE_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-download-action w-full py-4 px-6 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-3 cursor-pointer group"
                >
                  <Download className="w-5 h-5 stroke-[2.5] group-hover:translate-y-0.5 transition-transform" />
                  <span>DOWNLOAD v{CURRENT_VERSION} · ({DOWNLOAD_SIZE})</span>
                </a>
                <div className="flex flex-wrap items-center justify-between px-2 text-[11px] font-mono text-muted gap-2">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-jade" />
                    <span>Direct Google Drive High-Speed Link</span>
                  </span>
                  <a
                    href={GOOGLE_DRIVE_VIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-rose hover:text-rose-light underline"
                  >
                    Google Drive Mirror ↗
                  </a>
                </div>
              </div>

              {/* Optional Security Verification Link */}
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] flex items-center justify-between text-xs text-muted">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-jade shrink-0" />
                  <span>Publisher: <strong className="text-soft-white">Lashary Softwares</strong></span>
                </div>
                <button
                  onClick={() => {
                    navigate('/download');
                    onClose();
                  }}
                  className="text-rose hover:text-rose-light font-mono text-[11px] font-bold cursor-pointer"
                >
                  Security & Verification →
                </button>
              </div>

              {/* Secondary Navigation & Support Links */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                <button
                  onClick={() => {
                    navigate('/changelog');
                    onClose();
                  }}
                  className="text-muted hover:text-white transition-colors font-mono flex items-center gap-1.5 cursor-pointer"
                >
                  <Sparkles className="w-3.5 h-3.5 text-apricot" />
                  <span>View What's New in v{CURRENT_VERSION} →</span>
                </button>

                <a
                  href={contact.secondaryWhatsappUrl || contact.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-jade hover:text-jade-light transition-colors font-mono cursor-pointer"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>Chat with Aswad Lashari: {contact.secondaryWhatsappDisplay || "+92 371 043 4552"}</span>
                </a>
              </div>

            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DownloadModal;
